import React from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import NonModalWindow from '../NonModalWindow'
import {
  ArrowDownRightIcon,
  ArrowPathRoundedSquareIcon,
  ArrowUpRightIcon,
  ChevronLeftIcon,
  Cog6ToothIcon,
  InboxIcon,
  ListBulletIcon,
  PlusCircleIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid'
import { Button, ButtonText } from '../ui/button'
import { Description } from '../ui/Description'
import { Switch } from '../ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { Separator } from '../ui/separator'
// import useGetTransactionsByModel from '@/services/api/cache/useGetTransactionsByModel'
import TransactionCard from '../transactions/TransactionCard'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu'
import useGetModel from '@/services/api/model/useGetModel'
import useEnableModel from '@/services/api/model/useEnableModel'
import useDisableModel from '@/services/api/model/useDisableModel'
import useGetTransactionByFilter from '@/services/api/cache/useGetTransactionByFilter'

export default function ModelDetail({
  view,
}: {
  view: 'transactions' | 'settings'
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const type = searchParams.get('type') ?? 'all' // in|out|all
  const navigate = useNavigate()
  const { modelId } = useParams()

  // const { data } = useGetTransactionsByModel({ modelId, type })

  const { data, status: transactionStatus } = useGetTransactionByFilter({
    modelId,
    direction: type === 'all' ? undefined : type === 'in' ? 'in' : 'out',
  })
  const { mutate: enableModel, status: enableStatus } = useEnableModel()
  const { mutate: disableModel, status: disableStatus } = useDisableModel()
  const {
    data: model,
    status: modelStatus,
    isLoading: modelIsLoading,
  } = useGetModel({ id: modelId })

  const toggleModelStateInProgress =
    enableStatus === 'pending' || disableStatus === 'pending'

  function toggleModelState(val) {
    if (val) {
      enableModel(modelId)
    } else {
      disableModel(modelId)
    }
  }

  // if (modelStatus !== 'success') return 'Loading...'
  return (
    <NonModalWindow onClose={() => navigate('/')} isLoading={modelIsLoading}>
      <NonModalWindow.Header className="border-none">
        <div className="flex flex-1 gap-2">
          <div>
            <Button
              type="button"
              iconOnly
              variant="ghost"
              onClick={() => navigate(-1)}
              size="sm"
              className="size-7 self-start"
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
          </div>
          <div className="w-full">
            <div className="flex justify-between">
              <p>{model?.name}</p>
              <div className="mt-1">
                <Switch
                  disabled={toggleModelStateInProgress}
                  checked={model?.is_enabled}
                  onCheckedChange={toggleModelState}
                />
              </div>
            </div>
            <div className="mt-1 flex w-fit gap-2">
              <Description className="mt-0">{model?.description}</Description>
            </div>
          </div>
        </div>
        <Button
          type="button"
          iconOnly
          variant="ghost"
          onClick={() => navigate('/')}
          size="sm"
          className="size-7 self-start"
        >
          <XMarkIcon />
        </Button>
      </NonModalWindow.Header>
      <NonModalWindow.Body className="pt-2">
        <Tabs value={view}>
          <TabsList className="mb-3">
            <TabsTrigger value="transactions" className="flex gap-1">
              <PlusCircleIcon /> <span>Transactions</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex gap-1">
              <Cog6ToothIcon /> <span>Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="transactions" className="">
            <div>
              <div className="inline-flex rounded bg-gray-low text-sm text-txt-secondary">
                {['All', 'In', 'Out'].map((x) => (
                  <div
                    key={x}
                    onClick={() => {
                      searchParams.set('type', x.toLowerCase())
                      setSearchParams(searchParams, { replace: true })
                    }}
                    data-active={
                      x.toLocaleLowerCase() === type ? true : undefined
                    }
                    className="group flex cursor-pointer items-center [&>[data-slot=separator]]:last:hidden"
                  >
                    <div className=" px-2 py-1 group-data-[active]:border group-data-[active]:bg-white">
                      {x}
                    </div>
                    <Separator orientation="vertical" className="h-[80%] " />
                  </div>
                ))}
              </div>
              <div className="mt-4 grid gap-3">
                {transactionStatus === 'success' && data.length === 0 && (
                  <div className="grid h-[50vh] place-items-center">
                    <Description className="flex flex-col items-center text-center">
                      <InboxIcon className="size-5" />
                      <span className="text-sm">No Transactions</span>
                    </Description>
                  </div>
                )}
                {data.map((transaction) => (
                  <TransactionCard
                    key={transaction.id}
                    transaction={transaction}
                  />
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="">
            ss
          </TabsContent>
        </Tabs>
      </NonModalWindow.Body>
      <NonModalWindow.Footer>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="w-full">
              <ButtonText>Add Transaction</ButtonText>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem>
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  navigate(`/models/${modelId}/transactions/new?type=in`)
                }
              >
                <ArrowUpRightIcon />
                <ButtonText>Money In</ButtonText>
              </Button>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  navigate(`/models/${modelId}/transactions/new?type=out`)
                }
              >
                <ArrowDownRightIcon />
                <ButtonText>Money Out</ButtonText>
              </Button>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  navigate(`/models/${modelId}/transactions/new?type=transfer`)
                }
              >
                <ArrowPathRoundedSquareIcon />
                <ButtonText>Transfer</ButtonText>
              </Button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </NonModalWindow.Footer>
    </NonModalWindow>
  )
}
